import { CollectionViewer } from '@angular/cdk/collections';
import { LoadingState } from '@lcs/shared/api';
import { LcsMultiSelectDataSource } from '@lcs/shared/multi-select';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { EagerSelectActions } from '../../select/eager-select/eager-select-actions';
import { EagerSelectSelectors } from '../../select/eager-select/eager-select-selectors';

/**
 * Base class for eager multi-select data source implementations that simplifies the creation of data sources for new entities.
 */
export abstract class EagerMultiSelectDataSource<TItem, TApiResult = TItem> extends LcsMultiSelectDataSource<TItem> {
   allData: TItem[];

   allItemsSelected$ = new BehaviorSubject<boolean>(false);

   loading$: Observable<boolean>;

   public filterOverrides: string[] = [];

   constructor(
      protected store: Store<AppState>,
      protected actions: EagerSelectActions<TItem, TApiResult>,
      protected selectors: EagerSelectSelectors<TItem, AppState>
   ) {
      super();

      this.loading$ = store.pipe(
         select(selectors.selectCallState),
         map((callState) => {
            return callState === LoadingState.Loading;
         })
      );

      this.store.pipe(select(this.selectors.selectAll)).subscribe((data) => {
         return (this.allData = data);
      });
   }

   /**
    * A function that returns additional fields to include in the REST API request for options. Optional.
    */
   getAdditionalFields?(): string[];

   /**
    * A function that returns a url override to use for the REST API request for options. Optional.
    */
   getUrlOverride?(): string;

   getAllAvailableItems() {
      return this.allData;
   }

   connect(_collectionViewer: CollectionViewer): Observable<TItem[] | readonly TItem[]> {
      this.store.dispatch(
         this.actions.load({
            additionalFields: typeof this.getAdditionalFields === 'function' ? this.getAdditionalFields() : [],
            filterOverrides: this.filterOverrides,
            urlOverride: typeof this.getUrlOverride === 'function' ? this.getUrlOverride() : undefined,
         })
      );

      const selected$ = this.multiSelect$.pipe(
         switchMap((ms) => {
            return ms.selected$;
         })
      );
      const data$ = this.store.pipe(select(this.selectors.selectAll));

      return combineLatest([data$, selected$, this.searchTerm$]).pipe(
         map(([items, selectedItems, searchTerm]: [TItem[], readonly TItem[], string | null | undefined]): TItem[] => {
            const unselectedItems = items.filter((item) => {
               return !selectedItems.some((selectedUser) => {
                  return this.compareWith(item, selectedUser);
               });
            });
            if (unselectedItems.length === 0 && items.length > 0) {
               if (this.allItemsSelected$.value === false) {
                  this.allItemsSelected$.next(true);
               }
            } else if (this.allItemsSelected$.value === true) {
               this.allItemsSelected$.next(false);
            }

            searchTerm = searchTerm?.trim().toLowerCase();
            if (searchTerm) {
               if (!this.multiSelect.displayWith) {
                  throw Error(
                     'Eager multi-select data source requires the multi-select to have a displayWith method defined'
                  );
               }
               return unselectedItems.filter((item) => {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  const displayText = this.multiSelect.displayWith!(item);
                  if (displayText) {
                     // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                     return displayText.toLowerCase().includes(searchTerm!);
                  }
               });
            }
            return unselectedItems;
         })
      );
   }

   disconnect(_collectionViewer: CollectionViewer): void {
      // noop
   }

   /**
    * A function that checks the equality of two options.
    * @param a The first item to compare
    * @param b The second item to compare
    * @returns True if the options are equivalent. Otherwise false.
    */
   abstract compareWith(a: TItem, b: TItem): boolean;
}
